import Constants from '~/app/Constants';
import { CompanyConfig, Company } from '~/interfaces/CompanyConfig';

const companyConfigs: Record<Company, CompanyConfig> = {
  [Company.DEFAULT]: {
    type: Company.DEFAULT,
    clientId: process.env.REACT_APP_API_GATEWAY_CLIENTID,
    redirectUri: process.env.REACT_APP_API_GATEWAY_REDIRECT_URI,
    centralizationPath: Constants.PagesUrl.CentralizationPage,
    centralizationConfirmationPath: Constants.PagesUrl.CentralizationConfirmationPage,
    authorizeCallbackPath: Constants.PagesUrl.AuthorizeCallback,
  },
  [Company.IAAH]: {
    type: Company.IAAH,
    clientId: process.env.REACT_APP_API_GATEWAY_CLIENTID_IAAH,
    redirectUri: process.env.REACT_APP_API_GATEWAY_REDIRECT_URI_IAAH,
    centralizationPath: Constants.PagesUrl.CentralizationPageIaah,
    centralizationConfirmationPath: Constants.PagesUrl.CentralizationConfirmationPageIaah,
    authorizeCallbackPath: Constants.PagesUrl.AuthorizeCallbackIaah,
    externalDomain: process.env.REACT_APP_IAAH_EXTERNAL_DOMAIN,
    externalLogoffPath: '/s/logout',
  },
  [Company.IAGP]: {
    type: Company.IAGP,
    clientId: process.env.REACT_APP_API_GATEWAY_CLIENTID_IAGP,
    redirectUri: process.env.REACT_APP_API_GATEWAY_REDIRECT_URI_IAGP,
    centralizationPath: Constants.PagesUrl.CentralizationPageIAGP,
    centralizationConfirmationPath: Constants.PagesUrl.CentralizationConfirmationPageIAGP,
    authorizeCallbackPath: Constants.PagesUrl.AuthorizeCallbackIAGP,
    externalDomain: process.env.REACT_APP_IAGP_EXTERNAL_DOMAIN,
    externalLogoffPath: '/EEMWPNP1/logoff',
  },
  [Company.IAPW]: {
    type: Company.IAPW,
    clientId: process.env.REACT_APP_API_GATEWAY_CLIENTID_IAPW,
    redirectUri: process.env.REACT_APP_API_GATEWAY_REDIRECT_URI_IAPW,
    centralizationPath: Constants.PagesUrl.CentralizationPageIAPW,
    centralizationConfirmationPath: Constants.PagesUrl.CentralizationConfirmationPageIAPW,
    authorizeCallbackPath: Constants.PagesUrl.AuthorizeCallbackIAPW,
    externalDomain: process.env.REACT_APP_IAPW_EXTERNAL_DOMAIN,
    externalLogoffPath: '/EEMWPNP1/logoff',
  },
  [Company.INVESTIA]: {
    type: Company.INVESTIA,
    clientId: process.env.REACT_APP_API_GATEWAY_CLIENTID_INVESTIA,
    redirectUri: process.env.REACT_APP_API_GATEWAY_REDIRECT_URI_INVESTIA,
    centralizationPath: Constants.PagesUrl.CentralizationPageInvestia,
    centralizationConfirmationPath: Constants.PagesUrl.CentralizationConfirmationPageInvestia,
    authorizeCallbackPath: Constants.PagesUrl.AuthorizeCallbackInvestia,
    externalDomain: process.env.REACT_APP_INVESTIA_EXTERNAL_DOMAIN,
    externalLogoffPath: '/EEMWPNP1/logoff',
    loginAppDomain: process.env.REACT_APP_INVESTIA_LOGIN_APP_DOMAIN,
  },
};

export function getCompanyConfig(company: Company = Company.DEFAULT): CompanyConfig {
  return companyConfigs[company];
}

export function isAuthorizeCallbackPath(pathname: string): boolean {
  return Object.values(companyConfigs)
    .some((c) => c.authorizeCallbackPath === pathname);
}

export function getCompanyByPath(path: string): Company {
  if (path.includes('/centralization/iaah')) return Company.IAAH;
  if (path.includes('/centralization/iagp')) return Company.IAGP;
  if (path.includes('/centralization/iapw')) return Company.IAPW;
  if (path.includes('/centralization/investia')) return Company.INVESTIA;
  return Company.DEFAULT;
}

export function getCompanyOpenIdConfiguration(company: Company = Company.DEFAULT) {
  const config = getCompanyConfig(company);

  return {
    authority: process.env.REACT_APP_API_GATEWAY_AUTHORITY,
    client_id: config.clientId,
    scope:
      process.env.REACT_APP_API_GATEWAY_MOCK === 'true'
        ? 'mock-scope'
        : 'openid identity profile address phone email user_role wellnessprogram.readonly' +
          ' member-enrolment omni.availability omni.promotions omni.secure-messaging omni.products' +
          ' omni.documents omni.online-transactions omni.profile.password.v2 omni.profile',
    response_type: 'code',
    redirect_uri: config.redirectUri,
  };
}

export function getCentralizationConfirmationPath(path: string): string {
  const company = getCompanyByPath(path);
  return getCompanyConfig(company).centralizationConfirmationPath;
}

export function getExternalDomain(company: Company): string | undefined {
  const config = getCompanyConfig(company);
  return config.externalDomain;
}

export function getExternalBaseUrl(company: Company): string | undefined {
  const domain = getExternalDomain(company);
  return domain ? `https://${domain}` : undefined;
}

export function getExternalLogoffUrl(company: Company): string | undefined {
  const config = getCompanyConfig(company);
  const domain = getExternalDomain(company);

  if (!domain || !config.externalLogoffPath) return undefined;

  return `https://${domain}${config.externalLogoffPath}`;
}

export function getCompanyTenant(company: Company): string {
  switch (company) {
    case Company.DEFAULT:
      return 'ia';
    case Company.INVESTIA:
      return 'investia';
    case Company.IAAH:
      return 'iaah';
    case Company.IAGP:
    case Company.IAPW:
      return 'iawealth';
    default:
      return 'ia';
  }
}
